import * as XLSX from 'xlsx';

export type ExcelSheet = {
  name: string;
  rows: Array<Record<string, any>>;
};

// Convert JSON rows to a worksheet whose column widths fit the widest cell.
// Headers (and the width scan) come from the first row's keys.
const jsonToAutoWidthSheet = (rows: Array<Record<string, any>>): XLSX.WorkSheet => {
  const worksheet = XLSX.utils.json_to_sheet(rows);

  const headers = Object.keys(rows[0] || {});
  const colWidths: number[] = headers.map((header) => header.length);

  rows.forEach((row) => {
    headers.forEach((key, index) => {
      const cellValue = row[key];
      const cellString = cellValue !== undefined && cellValue !== null ? String(cellValue) : '';
      colWidths[index] = Math.max(colWidths[index], cellString.length);
    });
  });

  worksheet['!cols'] = colWidths.map((width) => ({
    wch: width + 2,
  }));

  return worksheet;
};

/**
 * Generates and downloads an Excel file with one worksheet per sheet entry.
 * Sheets with no rows are skipped; if every sheet is empty, nothing downloads.
 *
 * @param sheets - The worksheets to include, in order.
 * @param fileName - The name of the Excel file to download.
 */
export const downloadExcelWorkbookFromJson = (sheets: ExcelSheet[], fileName: string): void => {
  const populated = sheets.filter((sheet) => sheet.rows && sheet.rows.length > 0);
  if (populated.length === 0) {
    console.error('No data available for download.');
    return;
  }

  const workbook = XLSX.utils.book_new();
  populated.forEach((sheet) => {
    XLSX.utils.book_append_sheet(workbook, jsonToAutoWidthSheet(sheet.rows), sheet.name);
  });

  // Write workbook to a binary buffer
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  // Create a Blob and trigger a download
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  // Trigger the download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  console.log(`${fileName} downloaded successfully.`);
};

/**
 * Generates and downloads a single-sheet Excel file from JSON data.
 *
 * @param data - The JSON data to convert into an Excel file.
 * @param sheetName - The name of the Excel sheet.
 * @param fileName - The name of the Excel file to download.
 */
export const downloadExcelFromJson = (
  data: Array<Record<string, any>>,
  sheetName: string = 'Sheet1',
  fileName: string = 'ExportedData.xlsx',
): void => {
  downloadExcelWorkbookFromJson([{ name: sheetName, rows: data }], fileName);
};
